import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import { useMutation, useQueryClient } from 'react-query';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CircleIconSrc from 'src/assets/icons/circle.svg';
import TriangleIconSrc from 'src/assets/icons/triangle.svg';
import styled from 'styled-components';

import RectangleIconSrc from '@/assets/icons/square.svg';
import { editOption, getFiltersByRoadmapId, getOptionsByFilterId } from '@/features/canvas/api';
import { ColorPicker } from '@/features/canvas/components/ColorPicker';
import { FilterOption } from '@/features/canvas/components/FilterOption';
import {
  SizeButton,
  SizeButtonGroup,
  SizeButtonLabel,
} from '@/features/canvas/components/SizeCounter';
import { SizePicker } from '@/features/canvas/components/SizePicker';
import { DEFAULT_NODE_RADIUS } from '@/features/canvas/constants';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { getColorForSize } from '@/features/canvas/utils/size-to-color';
import { useShowToast } from '@/hooks/useShowToast';
import { components } from '@/types/api';

type FilterSingleOptionProps = {
  filterId: number;
  filterShape: 'circle' | 'square' | 'triangle';
  option: components['schemas']['FilterOption'];
  roadmapId: number;
  attributes?: React.HTMLAttributes<HTMLElement>;
  listeners?: React.DOMAttributes<Element>;
};

const FilterSingleOption = ({
  option,
  filterShape,
  filterId,
  roadmapId,
  attributes,
  listeners,
}: FilterSingleOptionProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showToast } = useShowToast();

  const { setIsSidebarContentLoading, updateLastSavedTime, setReorderedFilterOptions } =
    useEditorContext();

  const [pickerColor, setPickerColor] = useState<string>(option.color);
  const [pickerAnchorEl, setPickerAnchorEl] = useState<SVGElement | null>(null);

  const [sizeAnchorEl, setSizeAnchorEl] = useState<HTMLDivElement | null>(null);
  const [sizeValue, setSizeValue] = useState<number>(option.size ?? DEFAULT_NODE_RADIUS);

  useEffect(() => {
    setSizeValue(option.size ?? DEFAULT_NODE_RADIUS);
  }, [option.size]);

  const { mutateAsync: updateOption } = useMutation(
    async (optionData: components['schemas']['FilterOption']) => {
      try {
        await editOption(optionData.id!, optionData);
        updateLastSavedTime();
        showToast('success', t('editor.sidebar.filter.edit_option_success'));
      } catch (e) {
        showToast('success', t('editor.sidebar.filter.edit_option_error'));
      }
    }
  );

  const onClickColor = (event: React.MouseEvent<SVGElement>) => {
    setPickerAnchorEl(event.currentTarget);
  };

  const handleCloseColorPicker = async () => {
    setIsSidebarContentLoading(true);
    const updatedOption = { ...option, color: pickerColor };

    setPickerAnchorEl(null);

    await updateOption(updatedOption);

    setReorderedFilterOptions(prev => {
      const current = prev[filterId] || [];
      const updated = current.map(opt => (opt.id === updatedOption.id ? updatedOption : opt));
      return { ...prev, [filterId]: updated };
    });

    await queryClient.invalidateQueries([getOptionsByFilterId.name, filterId]);
    await queryClient.invalidateQueries([getFiltersByRoadmapId.name, roadmapId]);

    setIsSidebarContentLoading(false);
  };

  const handleEditColorPicker = (hex: string) => {
    setPickerColor(hex);
  };

  const onClickSize = (event: React.MouseEvent<HTMLDivElement>) => {
    setSizeAnchorEl(event.currentTarget);
  };

  const handleCloseSizePicker = async () => {
    setIsSidebarContentLoading(true);

    setSizeAnchorEl(null);

    const updatedOption = { ...option, color: pickerColor, size: sizeValue };
    await updateOption(updatedOption);

    setReorderedFilterOptions(prev => {
      const current = prev[filterId] || [];
      const updated = current.map(opt => (opt.id === updatedOption.id ? updatedOption : opt));
      return { ...prev, [filterId]: updated };
    });

    await queryClient.invalidateQueries([getOptionsByFilterId.name, filterId]);
    await queryClient.invalidateQueries([getFiltersByRoadmapId.name, roadmapId]);

    setIsSidebarContentLoading(false);
  };

  const handleEditSizePicker = (size: number) => {
    setSizeValue(size);
    setPickerColor(getColorForSize(size));
  };

  const getShapeIcon = () => {
    switch (filterShape) {
      case 'triangle':
        return <StyledSVG src={TriangleIconSrc} color={option.color} onClick={onClickColor} />;
      case 'square':
        return <StyledSVG src={RectangleIconSrc} color={option.color} onClick={onClickColor} />;
      default:
        return <StyledSVG src={CircleIconSrc} color={option.color} onClick={onClickColor} />;
    }
  };

  return (
    <OptionRow>
      <DragHandle className="drag-handle" aria-label="Drag" {...attributes} {...listeners}>
        <DragIndicatorIcon fontSize="small" />
      </DragHandle>
      <FilterOption
        key={option.id}
        option={option}
        filterId={filterId}
        roadmapId={roadmapId}
        Icon={getShapeIcon()}
        iconCursor
      />

      {option.size !== undefined && option.size !== null && (
        <SizeButtonGroup size="small" variant="contained" onClick={onClickSize}>
          <SizeButton sx={{ minWidth: 18 }}>
            <SizeButtonLabel variant="body1">{t('decrement')}</SizeButtonLabel>
          </SizeButton>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 26,
              height: 20,
              color: '#142e38',
              fontWeight: 400,
              border: '1px solid #d6d6d6',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            <Typography variant="body1">{sizeValue}</Typography>
          </Box>
          <SizeButton sx={{ minWidth: 18 }}>
            <SizeButtonLabel variant="body1">{t('increment')}</SizeButtonLabel>
          </SizeButton>
        </SizeButtonGroup>
      )}

      <ColorPicker
        anchorEl={pickerAnchorEl}
        handleClose={handleCloseColorPicker}
        handleEdit={handleEditColorPicker}
        pickerColor={pickerColor}
      />

      <SizePicker
        anchorEl={sizeAnchorEl}
        handleClose={handleCloseSizePicker}
        handleEdit={handleEditSizePicker}
        sizeValue={sizeValue}
        color={pickerColor}
      />
    </OptionRow>
  );
};

const StyledSVG = styled(SVG)`
  width: 1.4rem;
  height: 1.4rem;
  overflow: visible;
  & path {
    fill: ${({ color }) => color};
    stroke: ${({ color }) => color};
  }
`;

const OptionRow = styled.div<{ $isDragging?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  opacity: ${({ $isDragging }) => ($isDragging ? 0.6 : 1)};
  transform: ${({ $isDragging }) => ($isDragging ? 'scale(0.98)' : 'none')};
`;

const DragHandle = styled(props => <IconButton color="inherit" {...props} />)`
  cursor: grab;
  padding: 0;
  margin-right: 0.5rem;
  color: #707070;

  &:hover {
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

export { FilterSingleOption };
